/* eslint-disable max-classes-per-file */
import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";

import { AsyncSubject, Observable, ReplaySubject, of } from "rxjs";
import {
    catchError,
    filter,
    map,
    shareReplay,
    switchMap,
    take
} from "rxjs/operators";
import { RaskRoller } from "~app/common/models/constants";
import { IFeature } from "~app/common/models/feature-models";
import { IWebInstallningDTO } from "~app/common/models/webInstallning-models";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { environment } from "../../../../environments/environment";
import { ApiClientService } from "../api-client.service";
import { AppInsightsService } from "../app-insights.service";
import { FeatureService } from "../feature.service";
import { RaskVersionChecker } from "../rask-version-checker.service";

@Injectable({
    providedIn: "root"
})
export class AuthenticationService {

    userStateChanged = new ReplaySubject(1);
    userLogin = new ReplaySubject<User>();
    returnUrl: string;
    editRoles: string[] = [];
    private initializedSession$: Observable<boolean>;
    private user: User;
    private readonly apiClient = inject(ApiClientService);
    private readonly http = inject(HttpClient);
    private readonly appInsightsService = inject(AppInsightsService);
    private readonly featureService = inject(FeatureService);
    private readonly raskVersionChecker = inject(RaskVersionChecker);
    private readonly authService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly router = inject(Router);

    constructor() {
        this.userStateChanged.next(false);
    }

    msalIsReady(): Observable<boolean> {
        return this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                map(_ => true)
            );
    }

    initializeSession(): Observable<boolean> {
        if(this.initializedSession$) {
            return this.initializedSession$;
        }

        const reqHeaders = { headers: {

            "skip-cors-error-dialog" : "true"
        } };

        const msalIsReady = this.msalIsReady();
        this.initializedSession$ = msalIsReady.pipe(
            take(1),
            switchMap((_) =>
                this.http.get<User>(`${environment.apiV2}/authentication/afterlogin`, reqHeaders)),
            catchError((err) => {
                const { status } = err;
                if(status === 410) {
                    throw err;
                }
                return of(undefined);
            }),
            map(user => {
                if(ObjectUtils.isNullOrUndefined(user)) {
                    return false;
                }

                this.user = user;
                if (!this.hasRaskPermission()) {
                    this.router.navigate(["permissiontoraskdenied"]);
                    return false;
                }

                this.initUserLogIn(user);
                this.userLogin.next(user);
                return true;
            }),
            // om förfrågan lyckas cache:a resultatet
            shareReplay(1)
        );

        return this.initializedSession$;
    }

    logout(): AsyncSubject<void> {
        const onLogout = new AsyncSubject<void>();
        this.authService.logoutRedirect({
            postLogoutRedirectUri: "/core"
        }).subscribe(() => onLogout.next());

        return onLogout;
    }

    attachUserData(user: User) {
        this.user = user;
        AuthenticationAuthority.setUser(user);
        this.userStateChanged.next(this.authenticated());
    }

    initUserLogIn(user: User) {
        this.attachUserData(user);
        this.appInsightsService.onUserLogIn(user.userName, user.azureUserId);
        this.raskVersionChecker.authenticated(true);

        if (this.hasRaskPermission()) {
            this.featureService.initFeatures(user.features);
        } else {
            this.appInsightsService.trackEvent({ name: "onDeniedUserLogIn" }, { User: user.userName,
                AzureUserId: user.azureUserId });
        }
    }

    hasRole(role: string): boolean {
        const user = this.getUserData();
        return user ? user.roles.indexOf(role) > -1 : false;
    }

    hasOnlyRoles(roles: string[]): boolean {
        return this.user.roles.every(userRole => roles.includes(userRole));
    }

    hasRoles(roles: string | string[], all = false): boolean {
        const user = this.getUserData();
        const rolesArray = Array.isArray(roles) ? roles : roles.replace(/ /g, "").split(",");
        const rolesToCount = (all ? rolesArray.length : 1);
        return user ? user.roles.filter(i => (rolesArray.indexOf(i) > -1)).length >= rolesToCount : false;
    }

    hasAnyRole(roles: string | string[]): boolean {
        return this.hasRoles(roles, false);
    }

    hasAllRoles(roles: string | string[]): boolean {
        return this.hasRoles(roles, true);
    }

    canEdit(): boolean {
        if (!this.authenticated()) {
            return false;
        }
        if(this.editRoles.length === 0) {
            return true;
        }

        return this.hasAnyRole(this.editRoles);
    }

    authenticated() {
        const user = this.getUserData();
        return user !== undefined && user.roles.length > 0;
    }

    getUserData(): User {
        return this.user;
    }

    hasRaskPermission(): boolean {
        return this.hasAnyRole([
            RaskRoller.role.VirkeskopTitta,
            RaskRoller.role.Titta,
            RaskRoller.role.Kopplanera,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }
}

/*
Statisk klass som man kan fråga efter användaren om en sådan finns inloggad eller inte.
*/
export class AuthenticationAuthority {
    private static user: User;
    static getUser(): User {
        return this.user;
    }
    static clearUser(): void {
        this.user = undefined;
    }
    static setUser(user: User) {
        this.user = user;
    }
}

/*
    Class use to persist the logged in session
*/
export class User {
    twoFactorAuthenticated: boolean;
    userName: string;
    azureUserId: string;
    fullUserName: string;
    roles: string[];
    langugage: string;
    error: string;
    features: IFeature[];
    webInstallningar: IWebInstallningDTO[];
}
